import { lazy, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import { Layout } from "./components/Layout.js";
import { RequireAdmin } from "./components/RequireAdmin.js";
import { Home } from "./pages/Home.js";

/**
 * Route-level code splitting.
 *
 * Home is part of the entry chunk because it is the most common landing point.
 * Everything else is fetched on navigation, which keeps the Markdown renderer
 * (~50 KB gzipped) off the home page and the whole admin area out of the
 * bundle for every reader who is not Davis.
 */
const Reviews = lazy(() =>
  import("./pages/Reviews.js").then((m) => ({ default: m.Reviews })),
);
const Ratings = lazy(() =>
  import("./pages/Ratings.js").then((m) => ({ default: m.Ratings })),
);
const ReviewDetail = lazy(() =>
  import("./pages/ReviewDetail.js").then((m) => ({ default: m.ReviewDetail })),
);
const About = lazy(() =>
  import("./pages/About.js").then((m) => ({ default: m.About })),
);
/**
 * Both legal documents come from one module, so whichever a reader opens
 * first, the other is already downloaded — they cross-link to each other
 * constantly.
 */
const Terms = lazy(() =>
  import("./pages/Legal.js").then((m) => ({ default: m.Terms })),
);
const Privacy = lazy(() =>
  import("./pages/Legal.js").then((m) => ({ default: m.Privacy })),
);
const SignIn = lazy(() =>
  import("./pages/SignIn.js").then((m) => ({ default: m.SignIn })),
);
const SignUp = lazy(() =>
  import("./pages/SignUp.js").then((m) => ({ default: m.SignUp })),
);
const NotFound = lazy(() =>
  import("./pages/NotFound.js").then((m) => ({ default: m.NotFound })),
);
const AdminReviews = lazy(() =>
  import("./pages/admin/AdminReviews.js").then((m) => ({
    default: m.AdminReviews,
  })),
);
const ReviewEditor = lazy(() =>
  import("./pages/admin/ReviewEditor.js").then((m) => ({
    default: m.ReviewEditor,
  })),
);

/**
 * Browsers restore scroll position on history navigation but not on a fresh
 * push, so without this a reader who opens a review from halfway down the
 * index lands halfway down the article.
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return null;
}

/**
 * No Suspense boundary out here.
 *
 * It used to wrap the whole `<Routes>` tree, which meant `Layout` itself was
 * inside it — so every navigation to a lazily-loaded route tore the header,
 * nav and footer off the page, showed a bare spinner, and then rebuilt them.
 * Switching tabs flashed the entire chrome each time.
 *
 * The boundary now lives around `Outlet` inside Layout, so the chrome stays
 * mounted and only the page area suspends. See Layout.tsx.
 */
export default function App() {
  return (
    <>
      <ScrollToTop />

      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="reviews" element={<Reviews />} />
          <Route path="reviews/:slug" element={<ReviewDetail />} />
          <Route path="ratings" element={<Ratings />} />
          <Route path="about" element={<About />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="sign-in" element={<SignIn />} />
          <Route path="sign-up" element={<SignUp />} />

          <Route path="admin" element={<RequireAdmin />}>
            <Route index element={<AdminReviews />} />
            <Route path="reviews/:id" element={<ReviewEditor />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}
